import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../context/ProfileContext';
import { useTheme } from '../context/ThemeContext';

// Icons
const BackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m15 18-6-6 6-6" />
  </svg>
);

const BellIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const PhoneIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
    <line x1="12" y1="18" x2="12.01" y2="18" />
  </svg>
);

const UserIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const NotificationSettings = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { userProfile, activeProfile, activeProfileId } = useProfile();
  const { theme, isDark } = useTheme();
  const isNative = Capacitor.isNativePlatform();

  const [loading, setLoading] = useState(true);
  const [devices, setDevices] = useState([]);
  const [childrenDevices, setChildrenDevices] = useState([]); // { childName, childId, devices: [], notificationsEnabled: bool }
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingChildId, setSavingChildId] = useState(null); // Track which child toggle is saving

  // Determine which profile to show
  // If parent viewing as child -> only show that child's devices
  // If parent viewing as self -> show own devices + all children's devices
  // If child -> only show own devices
  const isParent = userProfile?.is_parent;
  const isViewingAsChild = activeProfileId && isParent && activeProfileId !== userProfile?.id;
  const displayProfileId = isViewingAsChild ? activeProfileId : userProfile?.id;
  const displayProfileName = isViewingAsChild ? activeProfile?.username : userProfile?.username;

  useEffect(() => {
    const loadDevices = async () => {
      if (!displayProfileId) return;

      try {
        // Get devices for the display profile
        const { data: profileDevices, error } = await supabase
          .from('user_devices')
          .select('id, onesignal_player_id, platform, created_at, last_active_at')
          .eq('user_id', displayProfileId)
          .order('last_active_at', { ascending: false });

        if (error) console.error('Error loading devices:', error);
        setDevices(profileDevices || []);

        // If parent viewing as self, also load children's devices
        if (isParent && !isViewingAsChild) {
          // Get all linked children
          const { data: familyLinks } = await supabase
            .from('family_links')
            .select('child_id, users:child_id(id, username, first_name)')
            .eq('parent_id', userProfile.id);

          if (familyLinks && familyLinks.length > 0) {
            const childrenData = [];

            for (const link of familyLinks) {
              const childId = link.child_id;
              const childName = link.users?.username || link.users?.first_name || 'Child';

              // Get devices for this child
              const { data: childDevices } = await supabase
                .from('user_devices')
                .select('id, onesignal_player_id, platform, created_at, last_active_at')
                .eq('user_id', childId)
                .order('last_active_at', { ascending: false });

              // Get notification preference for this child
              const { data: childPrefs } = await supabase
                .from('user_notification_preferences')
                .select('notifications_enabled')
                .eq('user_id', childId)
                .single();

              childrenData.push({
                childId,
                childName,
                devices: childDevices || [],
                notificationsEnabled: childPrefs?.notifications_enabled !== false,
              });
            }

            setChildrenDevices(childrenData);
          }
        }

        // Load notification preference
        const { data: prefs } = await supabase
          .from('user_notification_preferences')
          .select('notifications_enabled')
          .eq('user_id', displayProfileId)
          .single();

        setNotificationsEnabled(prefs?.notifications_enabled !== false);
      } catch (err) {
        console.error('Error loading notification settings:', err);
      } finally {
        setLoading(false);
      }
    };

    loadDevices();
  }, [displayProfileId, isParent, isViewingAsChild, userProfile?.id]);

  // Toggle notifications for current profile
  const handleToggleNotifications = async () => {
    setSaving(true);
    const newValue = !notificationsEnabled;
    setNotificationsEnabled(newValue);

    try {
      const { error } = await supabase
        .from('user_notification_preferences')
        .upsert({
          user_id: displayProfileId,
          notifications_enabled: newValue,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id',
        });

      if (error) {
        console.error('Error saving preference:', error);
        setNotificationsEnabled(!newValue); // Revert
      }
    } catch (err) {
      console.error('Error:', err);
      setNotificationsEnabled(!newValue);
    } finally {
      setSaving(false);
    }
  };

  // Toggle notifications for a specific child
  const handleToggleChildNotifications = async (childId) => {
    const child = childrenDevices.find(c => c.childId === childId);
    if (!child) return;

    setSavingChildId(childId);
    const newValue = !child.notificationsEnabled;

    // Optimistic update
    setChildrenDevices(prev => prev.map(c =>
      c.childId === childId ? { ...c, notificationsEnabled: newValue } : c
    ));

    try {
      const { error } = await supabase
        .from('user_notification_preferences')
        .upsert({
          user_id: childId,
          notifications_enabled: newValue,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id',
        });

      if (error) {
        console.error('Error saving child preference:', error);
        // Revert
        setChildrenDevices(prev => prev.map(c =>
          c.childId === childId ? { ...c, notificationsEnabled: !newValue } : c
        ));
      }
    } catch (err) {
      console.error('Error:', err);
      setChildrenDevices(prev => prev.map(c =>
        c.childId === childId ? { ...c, notificationsEnabled: !newValue } : c
      ));
    } finally {
      setSavingChildId(null);
    }
  };

  // Remove a device
  const handleRemoveDevice = async (deviceId) => {
    if (!window.confirm('Remove this device? It will no longer receive notifications.')) return;

    try {
      await supabase
        .from('user_devices')
        .delete()
        .eq('id', deviceId);

      setDevices(prev => prev.filter(d => d.id !== deviceId));
      setChildrenDevices(prev => prev.map(child => ({
        ...child,
        devices: child.devices.filter(d => d.id !== deviceId),
      })));
    } catch (err) {
      console.error('Error removing device:', err);
    }
  };

  // Format platform name
  const formatPlatform = (platform) => {
    if (platform === 'ios') return 'iPhone/iPad';
    if (platform === 'android') return 'Android';
    return platform || 'Unknown';
  };

  // Format last active
  const formatLastActive = (date) => {
    if (!date) return 'Never';
    const diff = Date.now() - new Date(date);
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    if (days > 30) return 'Over 30 days ago';
    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    return 'Just now';
  };

  // Copy to clipboard
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    alert(`${label} copied!`);
  };

  // Device card component
  const DeviceCard = ({ device, onRemove }) => (
    <div style={styles.deviceCard}>
      <div style={styles.deviceIcon}>
        <PhoneIcon />
      </div>
      <div style={styles.deviceInfo}>
        <span style={styles.devicePlatform}>{formatPlatform(device.platform)}</span>
        <span style={styles.deviceLastActive}>Last active: {formatLastActive(device.last_active_at)}</span>
        <span
          style={styles.devicePlayerId}
          onClick={() => copyToClipboard(device.onesignal_player_id, 'Player ID')}
        >
          {device.onesignal_player_id}
        </span>
      </div>
      <button style={styles.removeButton} onClick={() => onRemove(device.id)}>
        <TrashIcon />
      </button>
    </div>
  );

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: theme.bg,
      display: 'flex',
      flexDirection: 'column',
    },
    header: {
      backgroundColor: theme.bgCard,
      padding: '12px 16px',
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      borderBottom: `1px solid ${theme.border}`,
    },
    backButton: {
      background: 'none',
      border: 'none',
      padding: 8,
      cursor: 'pointer',
      color: theme.accent,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    headerTitle: {
      fontSize: 18,
      fontWeight: 600,
      color: theme.text,
      margin: 0,
    },
    content: {
      flex: 1,
      padding: 16,
      maxWidth: 600,
      margin: '0 auto',
      width: '100%',
    },
    // Master toggle section
    masterSection: {
      backgroundColor: theme.bgCard,
      borderRadius: 12,
      padding: 16,
      marginBottom: 16,
      border: `1px solid ${theme.border}`,
    },
    masterRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    masterInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
    },
    masterIcon: {
      width: 40,
      height: 40,
      borderRadius: 10,
      backgroundColor: isDark ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: theme.accent,
    },
    masterText: {
      display: 'flex',
      flexDirection: 'column',
    },
    masterTitle: {
      fontSize: 15,
      fontWeight: 600,
      color: theme.text,
    },
    masterSubtitle: {
      fontSize: 12,
      color: theme.textMuted,
    },
    toggle: {
      width: 48,
      height: 28,
      borderRadius: 14,
      border: 'none',
      cursor: 'pointer',
      position: 'relative',
      transition: 'background-color 0.2s',
      padding: 0,
    },
    toggleKnob: {
      width: 24,
      height: 24,
      borderRadius: 12,
      backgroundColor: '#fff',
      position: 'absolute',
      top: 2,
      left: 2,
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      transition: 'transform 0.2s',
    },
    // Profile section
    profileSection: {
      marginBottom: 20,
    },
    sectionHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      marginBottom: 12,
    },
    sectionIcon: {
      color: theme.accent,
    },
    sectionTitle: {
      fontSize: 14,
      fontWeight: 600,
      color: theme.text,
    },
    sectionCount: {
      fontSize: 12,
      color: theme.textMuted,
      marginLeft: 'auto',
    },
    // Device cards
    deviceCard: {
      backgroundColor: theme.bgCard,
      borderRadius: 10,
      padding: 12,
      marginBottom: 8,
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      border: `1px solid ${theme.border}`,
    },
    deviceIcon: {
      width: 36,
      height: 36,
      borderRadius: 8,
      backgroundColor: theme.bgSheet,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: theme.textSecondary,
    },
    deviceInfo: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
    },
    devicePlatform: {
      fontSize: 14,
      fontWeight: 500,
      color: theme.text,
    },
    deviceLastActive: {
      fontSize: 11,
      color: theme.textMuted,
    },
    devicePlayerId: {
      fontSize: 9,
      fontFamily: 'monospace',
      color: theme.textMuted,
      backgroundColor: theme.bgSheet,
      padding: '2px 6px',
      borderRadius: 4,
      marginTop: 4,
      cursor: 'pointer',
      wordBreak: 'break-all',
    },
    removeButton: {
      background: 'none',
      border: 'none',
      padding: 8,
      cursor: 'pointer',
      color: theme.error,
      opacity: 0.6,
    },
    // Empty state
    emptyState: {
      backgroundColor: theme.bgSheet,
      borderRadius: 10,
      padding: 20,
      textAlign: 'center',
    },
    emptyText: {
      fontSize: 13,
      color: theme.textMuted,
      margin: 0,
    },
    // Child section
    childSection: {
      marginTop: 24,
      marginBottom: 16,
    },
    childHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      marginBottom: 8,
      padding: '8px 12px',
      backgroundColor: isDark ? 'rgba(139, 92, 246, 0.1)' : 'rgba(139, 92, 246, 0.05)',
      borderRadius: 8,
    },
    childIcon: {
      color: '#8b5cf6',
    },
    childName: {
      fontSize: 13,
      fontWeight: 600,
      color: theme.text,
    },
    childCount: {
      fontSize: 11,
      color: theme.textMuted,
      marginLeft: 'auto',
      marginRight: 8,
    },
    childToggle: {
      width: 36,
      height: 22,
      borderRadius: 11,
      border: 'none',
      cursor: 'pointer',
      position: 'relative',
      transition: 'background-color 0.2s',
      padding: 0,
      flexShrink: 0,
    },
    childToggleKnob: {
      width: 18,
      height: 18,
      borderRadius: 9,
      backgroundColor: '#fff',
      position: 'absolute',
      top: 2,
      left: 2,
      boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
      transition: 'transform 0.2s',
    },
    // Loading
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 40,
    },
    spinner: {
      width: 32,
      height: 32,
      border: `3px solid ${theme.border}`,
      borderTop: `3px solid ${theme.accent}`,
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
    },
    // Info banner
    infoBanner: {
      backgroundColor: isDark ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)',
      borderRadius: 10,
      padding: 12,
      marginBottom: 16,
      border: `1px solid ${isDark ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)'}`,
    },
    infoText: {
      fontSize: 12,
      color: theme.textSecondary,
      margin: 0,
      lineHeight: 1.5,
    },
    // Debug section
    debugSection: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      marginBottom: 12,
      padding: '8px 12px',
      backgroundColor: theme.bgSheet,
      borderRadius: 8,
    },
    debugLabel: {
      fontSize: 11,
      fontWeight: 600,
      color: theme.textMuted,
      textTransform: 'uppercase',
    },
    debugValue: {
      flex: 1,
      fontSize: 10,
      fontFamily: 'monospace',
      color: theme.textSecondary,
      cursor: 'pointer',
      wordBreak: 'break-all',
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <button style={styles.backButton} onClick={() => navigate(-1)}>
            <BackIcon />
          </button>
          <h1 style={styles.headerTitle}>Manage Notifications</h1>
        </div>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner} />
        </div>
      </div>
    );
  }

  const totalDevices = devices.length + childrenDevices.reduce((sum, c) => sum + c.devices.length, 0);

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => navigate(-1)}>
          <BackIcon />
        </button>
        <h1 style={styles.headerTitle}>Manage Notifications</h1>
      </div>

      {/* Content */}
      <div style={styles.content}>
        {/* Info Banner */}
        {!isNative && (
          <div style={styles.infoBanner}>
            <p style={styles.infoText}>
              Push notifications are delivered to your iOS and Android devices.
              Open the app on your phone to register a device.
            </p>
          </div>
        )}

        {/* Debug: User ID */}
        <div style={styles.debugSection}>
          <span style={styles.debugLabel}>User ID:</span>
          <span
            style={styles.debugValue}
            onClick={() => copyToClipboard(displayProfileId, 'User ID')}
          >
            {displayProfileId}
          </span>
        </div>

        {/* Master Toggle */}
        <div style={styles.masterSection}>
          <div style={styles.masterRow}>
            <div style={styles.masterInfo}>
              <div style={styles.masterIcon}>
                <BellIcon />
              </div>
              <div style={styles.masterText}>
                <span style={styles.masterTitle}>Push Notifications</span>
                <span style={styles.masterSubtitle}>
                  {notificationsEnabled ? 'Enabled' : 'Disabled'} - {totalDevices} device{totalDevices !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
            <button
              style={{
                ...styles.toggle,
                backgroundColor: notificationsEnabled ? theme.accent : theme.border,
                opacity: saving ? 0.5 : 1,
              }}
              onClick={handleToggleNotifications}
              disabled={saving}
            >
              <div
                style={{
                  ...styles.toggleKnob,
                  transform: notificationsEnabled ? 'translateX(20px)' : 'translateX(0)',
                }}
              />
            </button>
          </div>
        </div>

        {/* Current Profile's Devices */}
        <div style={styles.profileSection}>
          <div style={styles.sectionHeader}>
            <PhoneIcon style={styles.sectionIcon} />
            <span style={styles.sectionTitle}>
              {isViewingAsChild ? `${displayProfileName}'s Devices` : 'Your Devices'}
            </span>
            <span style={styles.sectionCount}>{devices.length}</span>
          </div>

          {devices.length > 0 ? (
            devices.map(device => (
              <DeviceCard key={device.id} device={device} onRemove={handleRemoveDevice} />
            ))
          ) : (
            <div style={styles.emptyState}>
              <p style={styles.emptyText}>No devices registered</p>
            </div>
          )}
        </div>

        {/* Children's Devices (only for parents viewing as self) */}
        {isParent && !isViewingAsChild && childrenDevices.length > 0 && (
          <>
            {childrenDevices.map(child => (
              <div key={child.childId} style={styles.childSection}>
                <div style={styles.childHeader}>
                  <UserIcon style={styles.childIcon} />
                  <span style={styles.childName}>@{child.childName}</span>
                  <span style={styles.childCount}>{child.devices.length} device{child.devices.length !== 1 ? 's' : ''}</span>
                  <button
                    style={{
                      ...styles.childToggle,
                      backgroundColor: child.notificationsEnabled ? theme.accent : theme.border,
                      opacity: savingChildId === child.childId ? 0.5 : 1,
                    }}
                    onClick={() => handleToggleChildNotifications(child.childId)}
                    disabled={savingChildId === child.childId}
                  >
                    <div
                      style={{
                        ...styles.childToggleKnob,
                        transform: child.notificationsEnabled ? 'translateX(14px)' : 'translateX(0)',
                      }}
                    />
                  </button>
                </div>

                {child.devices.length > 0 ? (
                  child.devices.map(device => (
                    <DeviceCard key={device.id} device={device} onRemove={handleRemoveDevice} />
                  ))
                ) : (
                  <div style={styles.emptyState}>
                    <p style={styles.emptyText}>No devices registered for {child.childName}</p>
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default NotificationSettings;
